import { ESystemType } from '@core/api';
import { EInvoiceTaxMethod, EPaymentChannel, EProxyPayType, ESubOrderType, ETaxSourceType, IPageParam } from './common';
import { EAgreementStatusType } from './crowd-source/task-accept-package';

export interface ISettlementListParam extends IPageParam {
  /** 订单编号 */
  orderNo?: string;
  /** 任务名称 */
  dutyName?: string;
  /**
   * 支付通道
   * @description {@link EPaymentChannel}
   */
  payType: EPaymentChannel;
  /** 任务类型id */
  dutyTempId: string;
  /** 所属服务商 (众包灵工) */
  serverName?: string;
  /** 产品名称 (撮合灵工) */
  productName?: string;
  /**
   * 订单状态
   * @description {@link ESettlementStatus}
   */
  orderStatus?: ESettlementStatus;
  /**
   * 发票状态
   * @description {@link ESettlementInvoiceStatus}
   */
  invoiceStatus?: ESettlementInvoiceStatus;
  /** 企业 */
  companyName?: string;
  /** 所属代理商 */
  agentName?: string;
  /** 订单开始时间 */
  startTime?: string;
  /** 订单结束时间 */
  endTime?: string;
  /** 文件类型 */
  fileType?: ESettlementFileType;
  /** 支付备注 */
  remark?: string;
  /** 完成时间(起) */
  completeStartTime?: string;
  /** 完成时间(止) */
  completeEndTime?: string;
  /** 收入性质 */
  proxyPayType?: EProxyPayType;
  /** 税源地类型(0-大陆 1-海南) */
  taxSourceType?: ETaxSourceType;
  /** 订单类型 */
  orderSourceType?: ESubOrderType;
  /** 交付物状态 */
  deliverableStatus?: EDeliverableStatus;
}

export interface ISettlementListItem {
  /** 订单id */
  orderId: number;
  /** 订单编号 */
  orderNo: string;
  /** 任务名称 */
  dutyName: string;
  /** 任务类型 */
  dutyTempName: string;
  /** 企业 */
  companyName: string;
  /** 使用产品 */
  productCode: string;
  /** 所属服务商 (众包灵工) */
  serverName?: string;
  /** 产品名称 (撮合灵工) */
  productName?: string;
  /** 费用 (撮合灵工) */
  amount?: string;
  /**
   * 支付通道
   * @description {@link EPaymentChannel}
   */
  payType: EPaymentChannel;
  /** 总笔数 */
  totalNum: number;
  /** 订单总金额 */
  orderAmount: number;
  /** 成功金额 */
  successAmount: number;
  /** 失败金额 */
  failAmount: number;
  /** 结算金额 */
  settlementAmount: number;
  /** 企业服务费 */
  companyServiceAmount: number;
  /** 个人服务费 */
  personServiceAmount: number;
  /** 个人到账金额 */
  receivedAmount: number;
  /** 个税金额 */
  taxAmount: number;
  /** 订单创建时间 */
  startTime: string;
  /** 订单完成时间 */
  endTime: string;
  /**
   * 订单状态
   * @description {@link ESettlementStatus}
   */
  orderStatus: ESettlementStatus;
  /**
   * 发票状态
   * @description {@link ESettlementInvoiceStatus}
   */
  invoiceStatus: ESettlementInvoiceStatus;
  /** 支付备注 */
  remark: string;
  /** 回单url */
  receiptUrl?: string;
  /** 合同url */
  contractUrl?: string;
  /** 端口 */
  term: 'company' | 'agent' | 'saas' | 'server';
  /** 是否显示查看合同按钮 */
  showContractBtn: boolean;
  /** 是否显示支付按钮 */
  showPayBtn: boolean;
  /** 是否显示取消按钮 */
  showCancelBtn: boolean;
  /** 是否显示回单下载按钮 */
  showReceiptBtn: boolean;
  /** 发票编号 */
  invoiceNo?: string;
  /** 发票id */
  invoiceId?: number;
  /** 子订单类型 */
  orderSourceType?: ESubOrderType;
  /** 交付物状态 */
  deliverableStatus?: EDeliverableStatus;
  /** 交付物文件 */
  deliverFileList?: any[];
  /** 是否显示上传交付物按钮 */
  showDeliverablesBtn: boolean;
}

export enum ESettlementInvoiceStatus {
  /** 待申请 */
  InvoiceUnapply = 0,
  /** 待审核 */
  InvoicePending = 1,
  /** 待开票 */
  InvoiceUnpaid = 2,
  /** 已开票 */
  InvoiceSuccess = 3,
  /** 部分开票 */
  InvoicePartial = 4,
  /** 无需开票 */
  InvoiceNo = 5,
}

export const settlementInvoiceStatusLabel = {
  [ESettlementInvoiceStatus.InvoiceUnapply]: '待申请',
  [ESettlementInvoiceStatus.InvoicePending]: '待审核',
  [ESettlementInvoiceStatus.InvoiceUnpaid]: '待开票',
  [ESettlementInvoiceStatus.InvoiceSuccess]: '已开票',
  [ESettlementInvoiceStatus.InvoicePartial]: '部分开票',
  [ESettlementInvoiceStatus.InvoiceNo]: '无需开票',
};

export const settlementInvoiceStatusStyle = {
  [ESettlementInvoiceStatus.InvoiceUnapply]: 'info',
  [ESettlementInvoiceStatus.InvoicePending]: 'primary',
  [ESettlementInvoiceStatus.InvoiceUnpaid]: 'warning',
  [ESettlementInvoiceStatus.InvoiceSuccess]: 'success',
  [ESettlementInvoiceStatus.InvoicePartial]: 'primary',
  [ESettlementInvoiceStatus.InvoiceNo]: 'info',
};

export const settlementInvoiceStatusOptions = [
  {
    label: '待申请',
    value: ESettlementInvoiceStatus.InvoiceUnapply,
  },
  {
    label: '待审核',
    value: ESettlementInvoiceStatus.InvoicePending,
  },
  {
    label: '待开票',
    value: ESettlementInvoiceStatus.InvoiceUnpaid,
  },
  {
    label: '已开票',
    value: ESettlementInvoiceStatus.InvoiceSuccess,
  },
  {
    label: '部分开票',
    value: ESettlementInvoiceStatus.InvoicePartial,
  },
  {
    label: '无需开票',
    value: ESettlementInvoiceStatus.InvoiceNo,
  },
];

export enum ESettlementStatus {
  /** 待审核 */
  CHECK_PENDING = 'CHECK_PENDING',
  /** 审核驳回 */
  CHECK_REJECT = 'CHECK_REJECT',
  /** 待支付 */
  UNPAID = 'UNPAID',
  /** 定时发放 */
  TIME_PUSH = 'TIME_PUSH',
  /** 支付中 */
  DURING_PAY = 'DURING_PAY',
  /** 交易成功 */
  Success = 'SUCCEED_PAY',
  /** 交易失败 */
  Fail = 'FAIL_PAY',
  /** 交易取消 */
  CANCEL_PAY = 'CANCEL_PAY',
  /** 退汇 */
  REEXCHANGE = 'REEXCHANGE',
  /** 部分成功 */
  OVER = 'OVER',
}

export const settlementStatusLabel = {
  [ESettlementStatus.CHECK_PENDING]: '待审核',
  [ESettlementStatus.CHECK_REJECT]: '审核驳回',
  [ESettlementStatus.UNPAID]: '待支付',
  [ESettlementStatus.TIME_PUSH]: '定时发放',
  [ESettlementStatus.DURING_PAY]: '支付中',
  [ESettlementStatus.Success]: '交易成功',
  [ESettlementStatus.Fail]: '交易失败',
  [ESettlementStatus.CANCEL_PAY]: '交易取消',
  [ESettlementStatus.REEXCHANGE]: '退汇',
  [ESettlementStatus.OVER]: '部分成功',
};

export const settlementStatusStyle = {
  [ESettlementStatus.CHECK_PENDING]: 'primary',
  [ESettlementStatus.CHECK_REJECT]: 'danger',
  [ESettlementStatus.UNPAID]: 'primary',
  [ESettlementStatus.TIME_PUSH]: 'primary',
  [ESettlementStatus.DURING_PAY]: 'primary',
  [ESettlementStatus.Success]: 'success',
  [ESettlementStatus.Fail]: 'danger',
  [ESettlementStatus.CANCEL_PAY]: 'info',
  [ESettlementStatus.REEXCHANGE]: 'primary',
  [ESettlementStatus.OVER]: 'primary',
};

export const settlementStatusOptions = [
  {
    label: settlementStatusLabel[ESettlementStatus.CHECK_PENDING],
    value: ESettlementStatus.CHECK_PENDING,
  },
  {
    label: settlementStatusLabel[ESettlementStatus.CHECK_REJECT],
    value: ESettlementStatus.CHECK_REJECT,
  },
  {
    label: settlementStatusLabel[ESettlementStatus.UNPAID],
    value: ESettlementStatus.UNPAID,
  },
  {
    label: settlementStatusLabel[ESettlementStatus.TIME_PUSH],
    value: ESettlementStatus.TIME_PUSH,
  },
  {
    label: settlementStatusLabel[ESettlementStatus.DURING_PAY],
    value: ESettlementStatus.DURING_PAY,
  },
  {
    label: settlementStatusLabel[ESettlementStatus.Success],
    value: ESettlementStatus.Success,
  },
  {
    label: settlementStatusLabel[ESettlementStatus.Fail],
    value: ESettlementStatus.Fail,
  },
  {
    label: settlementStatusLabel[ESettlementStatus.CANCEL_PAY],
    value: ESettlementStatus.CANCEL_PAY,
  },
  // {
  //   label: settlementStatusLabel[ESettlementStatus.REEXCHANGE],
  //   value: ESettlementStatus.REEXCHANGE,
  // },
  {
    label: settlementStatusLabel[ESettlementStatus.OVER],
    value: ESettlementStatus.OVER,
  },
];

export const settlementDetailStatusOpts = [
  {
    label: settlementStatusLabel[ESettlementStatus.Success],
    value: ESettlementStatus.Success,
  },
  {
    label: settlementStatusLabel[ESettlementStatus.Fail],
    value: ESettlementStatus.Fail,
  },
  {
    label: settlementStatusLabel[ESettlementStatus.REEXCHANGE],
    value: ESettlementStatus.REEXCHANGE,
  },
];

export interface ISettlementDetailListParam extends IPageParam {
  /** 订单id */
  orderId?: number;
  /** 订单编号 */
  orderNo: string;
  /** 子订单编号 */
  orderDetailNo: string;
  /** 企业 */
  companyName: string;
  /** 所属代理商 */
  agentName?: string;
  /**
   * 发票状态
   * @description {@link ESettlementInvoiceStatus}
   */
  invoiceStatus: ESettlementInvoiceStatus;
  /** 任务名称 */
  dutyName: string;
  /** 所属服务商 (众包灵工) */
  serverName?: string;
  /** 产品名称 (撮合灵工) */
  productName?: string;
  /** 身份证号 */
  personIdNumber: string;
  /** 手机号 */
  personTel: string;
  /** 银行卡号 */
  personBank: string;
  /**
   * 交易状态
   * @description {@link ESettlementStatus}
   */
  orderDetailStatus: ESettlementStatus;
  /**
   * 支付通道
   * @description {@link EPaymentChannel}
   */
  payType: EPaymentChannel;
  /** 订单开始时间 */
  startTime: string;
  /** 订单结束时间 */
  endTime: string;
  /** 交易附言 */
  remark?: string;
  /** 三方订单编号 */
  outOrderDetailNo?: string;
  /** 订单完成时间(起) */
  completeStartTime?: string;
  /** 订单完成时间(止) */
  completeEndTime?: string;
  /** 合同状态 */
  contractStatus?: EAgreementStatusType;
  /** 收款人 */
  personName?: string;
  /** 订单状态列表 */
  orderDetailStatusList?: ESettlementStatus[];
  /** 收入性质 */
  proxyPayType?: EProxyPayType;
  /** 税源地类型(0-大陆 1-海南) */
  taxSourceType?: ETaxSourceType;
  /** 开票方式 */
  invoiceTaxMethod?: EInvoiceTaxMethod;
  /** 子订单类型 */
  orderSourceType?: ESubOrderType;
}

export interface ISettlementDetailListItem {
  /** 订单id */
  orderDetailId: number;
  /** 订单编号 */
  orderNo: string;
  /** 子订单编号 */
  orderDetailNo: string;
  /** 企业 */
  companyName: string;
  /** 任务名称 */
  dutyName: string;
  /** 使用产品 */
  productCode: string;
  /** 所属服务商 (众包灵工) */
  serverName?: string;
  /** 产品名称 (撮合灵工) */
  productName?: string;
  /** 费用 (撮合灵工) */
  amount?: string;
  /** 开票科目 */
  invoiceSubName: string;
  /**
   * 支付通道
   * @description {@link EPaymentChannel}
   */
  payType: EPaymentChannel;
  /** 收款人 */
  personName: string;
  /** 身份证号 */
  personIdNumber: string;
  /** 手机号 */
  personTel: string;
  /** 银行卡号 */
  personBank: string;
  /** 订单总金额 */
  orderAmount: number;
  /** 结算金额 */
  settlementAmount: number;
  /** 个人到账金额 */
  receivedAmount: number;
  /** 企业服务费 */
  companyServiceAmount?: number;
  /** 初始企业服务费 */
  initialCompanyServiceAmount?: number;
  /** 个人服务费 */
  personServiceAmount?: number;
  /** 个税金额 */
  taxAmount: number;
  /**
   * 交易状态
   * @description {@link ESettlementStatus}
   */
  orderDetailStatus: ESettlementStatus;
  /**
   * 发票状态
   * @description {@link ESettlementInvoiceStatus}
   */
  invoiceStatus: ESettlementInvoiceStatus;
  /** 订单创建时间 */
  startTime: string;
  /** 订单完成时间 */
  endTime: string;
  /** 交易附言 */
  remark: string;
  /** 失败原因 */
  detailFailReason: string;
  /** 是否显示取消按钮 */
  receiptUrl?: string;
  /** 是否显示回单下载按钮 */
  showReceiptBtn: boolean;
  /** 发票编号 */
  businessNo?: string;
  /** 发票id */
  applyId?: number;
  /** 合同url */
  contractUrl?: string;
  /** 是否显示查看合同按钮 */
  showContractBtn: boolean;
  /** 撮合子订单合同状态 0:未签署 1:已签署 */
  contractStatus: EAgreementStatusType;
  /** 开票方式 */
  invoiceTaxMethod?: EInvoiceTaxMethod;
  /** 结算单下载url */
  crowdSubContractUrl?: string;
  /** 是否展示结算单下载按钮 */
  showSettlementBtn?: boolean;
  /** 子订单类型 */
  subOrderType?: ESubOrderType;
}

/**
 * 结算订单日志信息
 */
export interface ISettlementOrderLog {
  /** 日志内容 */
  operationContext: string;
  /** 操作时间 */
  logTime: string;
  /** 操作人 */
  creator: string;
}

/**
 * 结算订单任务信息
 */
export interface ISettlementOrderDuty {
  /** 任务合同名称 */
  contractName: string;
  /** 任务合同url */
  contractUrl: string;
  /** 任务id */
  dutyId: number;
  /** 订单id */
  orderId: number;
  /** 订单编号 */
  orderNo: string;
  /**
   * 订单状态
   * @description {@link ESettlementStatus}
   */
  orderStatus: ESettlementStatus;
  /** 任务名称 */
  dutyName: string;
  /** 任务类型 */
  dutyTempName: string;
  /** 企业名称 */
  companyName: string;
  /** 使用产品 */
  productCode: string;
  /** 产品名称 (撮合灵工) */
  productName?: string;
  /** 开票科目 */
  invoiceSubName: string;
  /** 发票类型 */
  invoiceTypeStr: string;
  /**
   * 支付通道
   * @description {@link EPaymentChannel}
   */
  payType: EPaymentChannel;
  /** 订单开始时间 */
  startTime: string;
  /** 订单结束时间 */
  endTime: string;
  /** 结算说明 */
  remark: string;
  /** 收款方式 0: 银行卡 1: 支付宝 */
  receivedType: 0 | 1;
  /** 税源地类型 */
  taxSourceType?: ETaxSourceType;
  /** 收入性质 */
  proxyPayType?: EProxyPayType;
  /** 服务商名称 */
  serverName?: string;
  /** 开票方式 */
  invoiceTaxMethod?: EInvoiceTaxMethod;
}

/**
 * 结算订单详情
 */
export interface ISettlementOrderDetail {
  /** 总笔数 */
  totalNum: number;
  /** 订单总金额 */
  orderAmount: number;
  /** 成功金额 */
  successAmount: number;
  /** 失败金额 */
  failAmount: number;
  /** 结算金额 */
  settlementAmount: number;
  /** 服务费 */
  serviceAmount: number;
  /** 企业服务费 */
  companyServiceAmount: number;
  /** 个人服务费 */
  personServiceAmount: number;
  /** 个人到账金额 */
  receivedAmount: number;
  /** 收款方式 0: 银行卡 1: 支付宝 */
  receivedType: 0 | 1;
  /** 订单合同名称 */
  contractName: string;
  /** 订单合同url */
  contractUrl: string;
  /** 个税金额 */
  taxAmount: number;
  /** 订单id */
  orderId: number;
  /** 订单编号 */
  orderNo: string;
  /** 任务信息 */
  duty: ISettlementOrderDuty;
  /** 订单日志列表 */
  orderLogs: ISettlementOrderDetail[];
  /** 税源地类型 */
  taxSourceType?: ETaxSourceType;
  /** 开票方式 */
  invoiceTaxMethod?: EInvoiceTaxMethod;
}

/**
 * 任务信息详情
 */
export interface ISettlementDutyDetail {
  /** 任务ID */
  id: number;
  /** 任务名称 */
  dutyName: string;
  /** 任务模版主键 */
  dutyTempId: number;
  /** 任务模版名称 */
  tempName: string;
  /** 开票科目主键 */
  invoiceSubId: number;
  /** 开票科目名称 */
  invoiceSubName: string;
  /** 税源地类型 */
  taxSourceType: ETaxSourceType;
  /** 收入性质 */
  proxyPayType: EProxyPayType;
  /** 服务商名称 */
  serverName: string;
}

/**
 * 结算支付单详细信息
 */
export interface ISettlementOrderPayInfo {
  /** 订单ID */
  orderId: number;
  /** 订单总金额 */
  orderAmount: number;
  /** 余额 */
  balance: number;
  /** 结算总金额 */
  settlementAmount: number;
  /** 总服务费 */
  serviceAmount: number;
  /** 企业服务费 */
  companyServiceAmount: number;
  /** 个人服务费 */
  personServiceAmount: number;
  /** 到账总金额 */
  receivedAmount: number;
  /** 总税金 */
  taxAmount: number;
  /** 费用 */
  amount: number;
  /** 结算说明 */
  remark: string;
  /** 总笔数 */
  totalNum: number;
  /** 手机号 */
  phone: string;
  /** 账户信息 */
  accountInfos: Array<ISOrderPayAccountInfo>;
  /**
   * 支付类型集合 (0: 招商银行 1: 连连支付)
   * @description {@link EPaymentChannel}
   */
  payTypes: EPaymentChannel[];
}

/**
 * 账户信息
 */
export interface ISOrderPayAccountInfo {
  /** 支付类型 */
  payType: EPaymentChannel;
  /** 共管账户(服务商账户+记账子单元编号)/连连账户 */
  accountNo: string;
  /** 招商子单元id/连连账户id */
  accountId: string;
  /** 账户余额 */
  balance: number;
  /** 账户名称 */
  accountName: string;
}

/**
 * 结算订单支付请求参数
 */
export interface ISettlementOrderPayParam {
  /** 订单ID */
  orderId: number;
  /**
   * 支付类型
   * @description {@link EPaymentChannel}
   */
  payType?: EPaymentChannel;
  /**
   * 发放模式
   * @remark 0: 立即发放 1: 定时发放
   */
  sendMode?: 0 | 1;
  /** 结算说明 */
  remark: string;
  /** 支付密码 (RSA加密后传输) */
  password: string;
  /** 随机因子 (加密因子) */
  randomKey?: string;
  /** 短信验证码 */
  code: string;
  /** 定时发放时间 (当sendMode=1时必填) */
  taskTime?: string;
}

export interface IInvoiceDetail {
  /** 发票id */
  invoiceId: number;
  /** 任务类型 0 众包 1 撮合 */
  dutyType?: 0 | 1;
  /** 发票类型  (0:专票，1：普票) */
  invoiceType?: 0 | 1;
  /** 开票备注 */
  invoiceRemark: string;
  /** 申请开票原因 */
  invoiceApplyReason?: string;
  /** 开票状态 0 未开票 1 已开票 */
  invoiceStatus: any;
  /** 产品编码 */
  productCode: string;
  /** 发票科目名称 */
  invoiceSubName: string;
  /** 合并订单数 */
  orderNum: number;
  /** 开票金额 */
  invoiceAmount: number;
  /** 开票技术服务费 */
  serviceAmount: number;
  /** 合并订单金额 */
  orderAmount: number;
  /** 联系人姓名 */
  contactName: string;
  /** 联系人手机号 */
  contactPhone: string;
  /** 联系人邮箱 */
  contactEmail: string;
  /** 邮寄地址 */
  contactAddress: string;
  /** 开票订单信息 */
  orderList: IInvoiceOrderInfo[];
  /** 任务名称 */
  dutyName?: string;
  /** 税源地类型 */
  taxSourceType?: ETaxSourceType;
  /** 收入性质 */
  proxyPayType?: EProxyPayType;
  /** 服务商名称 */
  serverName?: string;
  /** 产品名称 */
  productName?: string;
  /** 开票方式 */
  invoiceTaxMethod?: EInvoiceTaxMethod;
}

export interface IInvoiceOrderInfo {
  /** 订单id */
  orderId: number;
  /** 订单编号 */
  orderNo: string;
  /** 产品编码 */
  productCode: string;
  /** 订单金额 */
  orderAmount: number;
  /** 发放成功金额 */
  successOrderAmount: number;
  /** 可开票技术服务费 */
  serviceAmount: number;
  /** 订单完成时间 */
  endTime: string;
}

/**
 * 开票申请请求参数
 */
export interface IInvoiceApplyRequest {
  /** 任务id */
  dutyId: number;
  /** 任务类型 0 众包 1 撮合 */
  dutyType?: 0 | 1;
  /** 企业id */
  companyId: number;
  /** 发票类型:0专票、1普票 */
  invoiceType: number;
  /** 开票备注 */
  invoiceRemark?: string;
  /** 联系人姓名 */
  contactName: string;
  /** 联系人手机号 */
  contactPhone?: string;
  /** 联系人邮箱 */
  contactEmail?: string;
  /** 邮寄地址 */
  contactAddress?: string;
  /** 订单id集合 */
  orderIds: number[];
}

export interface ICrowdOrderDetailNo {
  /** 子订单id */
  orderDetailId: number;
  /** 订单编号 */
  orderDetailNo: number;
}

export enum ESettlementFileType {
  /** 众包订单回单 */
  CROWD_ORDER_RECEIPT = 'CROWD_ORDER_RECEIPT',
  /** 众包子订单回单 */
  CROWD_ORDER_DETAIL_RECEIPT = 'CROWD_ORDER_DETAIL_RECEIPT',
  /** 撮合订单回单 */
  MATCH_ORDER_RECEIPT = 'MATCH_ORDER_RECEIPT',
  /**  撮合子订单回单 */
  MATCH_ORDER_DETAIL_RECEIPT = 'MATCH_ORDER_DETAIL_RECEIPT',
  /** 充值/提现回单 */
  CASH_OR_RECHARGE_FUND_RECEIPT = 'CASH_OR_RECHARGE_FUND_RECEIPT',
  /** 撮合完税发票 */
  MATCH_TAX_INVOICE = 'MATCH_TAX_INVOICE',
  /** 撮合完税证明 */
  MATCH_TAX_CLEARANCE_CERTIFICATE = 'MATCH_TAX_CLEARANCE_CERTIFICATE',
  /** 线下开票申请资料 */
  CROWD_TAX_INVOICE = 'CROWD_TAX_INVOICE',
  /** 众包完税证明 */
  CROWED_TAX_CLEARANCE_CERTIFICATE = 'CROWED_TAX_CLEARANCE_CERTIFICATE',
}

export const settlementFileTypeLabel = {
  [ESettlementFileType.CROWD_ORDER_RECEIPT]: '众包订单回单',
  [ESettlementFileType.CROWD_ORDER_DETAIL_RECEIPT]: '众包子订单回单',
  [ESettlementFileType.MATCH_ORDER_RECEIPT]: '撮合订单回单',
  [ESettlementFileType.MATCH_ORDER_DETAIL_RECEIPT]: '撮合子订单回单',
  [ESettlementFileType.CASH_OR_RECHARGE_FUND_RECEIPT]: '充值/提现回单',
  [ESettlementFileType.MATCH_TAX_INVOICE]: '撮合完税发票',
  [ESettlementFileType.MATCH_TAX_CLEARANCE_CERTIFICATE]: '撮合完税证明',
  [ESettlementFileType.CROWD_TAX_INVOICE]: '线下开票申请资料',
  [ESettlementFileType.CROWED_TAX_CLEARANCE_CERTIFICATE]: '众包完税证明',
};

/**
 * 结算统计信息
 */
export interface ISettlementStatistics {
  /** 总笔数 */
  totalNum: number;
  /** 成功笔数 */
  successNum: number;
  /** 失败笔数 */
  failNum: number;
  /** 总金额 */
  orderAmount: number;
  /** 成功总金额 */
  successAmount: number;
  /** 失败总金额 */
  failAmount: number;
  /** 结算总金额 */
  settlementAmount: number;
  /** 到账金额 */
  receivedAmount: number;
  /** 企业服务费 */
  serviceAmount: number;
  /** 企业服务费 */
  companyServiceAmount?: number;
  /** 个人服务费 */
  personServiceAmount: number;
  /** 个税金额 */
  taxAmount: number;
}

/**
 * 结算订单明细详情
 */
export interface ISettlementOrderDetailObj {
  /** 订单id */
  orderDetailId?: number;
  /** 收款人 */
  personName?: string;
  /** 线下导入订单-证件类型 */
  idType?: ESettlementDetailIdType;
  /** 线下导入订单-证件号 */
  idNo?: string;
  /** 线下导入订单-证件照正面图片URL */
  idFrontUrl?: string;
  /** 线下导入订单-证件照背面图片URL */
  idBackUrl?: string;
  /** 线下导入订单-回单URL */
  receiptUrl?: any[];
  /** 线下导入订单-自主上传合同URL */
  contractUrl?: any[];
}

/**
 * 证件类型
 */
export enum ESettlementDetailIdType {
  /** 外国护照 */
  FOREIGN_PASSPORT = 'FOREIGN_PASSPORT',
  /** 中国护照 */
  CHINA_PASSPORT = 'CHINA_PASSPORT',
  /** 外国人永久居留身份证（外国人永久居留证） */
  FOREIGN_PERMANENT_RESIDENCE_ID = 'FOREIGN_PERMANENT_RESIDENCE_ID',
  /** 港澳居民来往内地通行证 */
  HONGKONG_MACAO_PASS = 'HONGKONG_MACAO_PASS',
  /** 港澳居民来往内地通行证（非中国籍） */
  HONGKONG_MACAO_PASS_FOREIGN = 'HONGKONG_MACAO_PASS_FOREIGN',
  /** 台湾居民来往大陆通行证 */
  TAIWAN_PASS = 'TAIWAN_PASS',
  /** 中华人民共和国港澳居民居住证 */
  HONGKONG_MACAO_RESIDENCE_PERMIT = 'HONGKONG_MACAO_RESIDENCE_PERMIT',
  /** 中华人民共和国台湾居民居住证 */
  TAIWAN_RESIDENCE_PERMIT = 'TAIWAN_RESIDENCE_PERMIT',
  /** 中华人民共和国外国人工作许可证（A类） */
  FOREIGN_WORK_PERMIT_A = 'FOREIGN_WORK_PERMIT_A',
  /** 中华人民共和国外国人工作许可证（B类） */
  FOREIGN_WORK_PERMIT_B = 'FOREIGN_WORK_PERMIT_B',
  /** 中华人民共和国外国人工作许可证（C类） */
  FOREIGN_WORK_PERMIT_C = 'FOREIGN_WORK_PERMIT_C',
  /** 其他个人证件 */
  OTHER_PERSONAL_ID = 'OTHER_PERSONAL_ID'
}

export const settlementDetailIdTypeLabel = {
  [ESettlementDetailIdType.FOREIGN_PASSPORT]: '外国护照',
  [ESettlementDetailIdType.CHINA_PASSPORT]: '中国护照',
  [ESettlementDetailIdType.FOREIGN_PERMANENT_RESIDENCE_ID]: '外国人永久居留身份证（外国人永久居留证）',
  [ESettlementDetailIdType.HONGKONG_MACAO_PASS]: '港澳居民来往内地通行证',
  [ESettlementDetailIdType.HONGKONG_MACAO_PASS_FOREIGN]: '港澳居民来往内地通行证（非中国籍）',
  [ESettlementDetailIdType.TAIWAN_PASS]: '台湾居民来往大陆通行证',
  [ESettlementDetailIdType.HONGKONG_MACAO_RESIDENCE_PERMIT]: '中华人民共和国港澳居民居住证',
  [ESettlementDetailIdType.TAIWAN_RESIDENCE_PERMIT]: '中华人民共和国台湾居民居住证',
  [ESettlementDetailIdType.FOREIGN_WORK_PERMIT_A]: '中华人民共和国外国人工作许可证（A类）',
  [ESettlementDetailIdType.FOREIGN_WORK_PERMIT_B]: '中华人民共和国外国人工作许可证（B类）',
  [ESettlementDetailIdType.FOREIGN_WORK_PERMIT_C]: '中华人民共和国外国人工作许可证（C类）',
  [ESettlementDetailIdType.OTHER_PERSONAL_ID]: '其他个人证件',
};
export interface ISettlementReadOrderImportParams{
  /** 任务id */
  dutyId: number;
  /** 模板附件url */
  templateUrl: string;
  /** 企业ID */
  companyId: number;
}

export interface ISettlementOrderImportItem {
  /** 收款人 */
  personName: string;
  /** 结算金额 */
  settlementAmount: number;
  /** 到账金额 */
  receivedAmount: number;
  /** 订单总金额 */
  orderAmount: number;
  /** 服务费 */
  serviceAmount: number;
  /** 企业服务费 */
  companyServiceAmount: number;
  /** 个人服务费 */
  personServiceAmount: number;
  /** 服务费率 */
  serviceRate: number;
  /** 个税金额 */
  taxAmount: number;
  /** 支付通道 */
  payType: number;
  /** 手机号 */
  personTel: string;
  /** 到账方式(0银行卡 1支付宝) */
  receivedType: number;
  /** 银行卡号 */
  personBank: string;
  /** 证件类型  */
  idType: string;
  /** 证件号 */
  idNo: string;
  /** 校验结果 0:不通过 1:通过  */
  result: number;
  /** 校验不通过原因 */
  errorMsg: string;
  /** 是否重复 0:否 1:是 */
  repeatStatus: 0 | 1;
}

export interface ISettlementOrderImportParams {
  /** 任务id */
  dutyId: number;
  /** 文件名称	 */
  excelName?: string;
  /** 企业ID */
  companyId: number;
  /** 人员信息 */
  orderDetailList?: ISettlementOrderImportItem[];
}

/**
 * 服务费支付失败订单列表项
 */
export interface IErrServiceFeeOrderItem {
  /** 订单id */
  orderId: number;
  /** 子订单id */
  orderDetailId: number;
  /** 订单编号 */
  orderNo: string;
  /** 子订单编号 */
  orderDetailNo: string;
  /** 服务主体 */
  productCode: string;
  /** 公司id */
  companyId: number;
  /** 公司名称 */
  companyName: string;
  /** 服务商id */
  serverId: number;
  /** 服务商名称 */
  serverName: string;
  /** 技术服务费 */
  techServiceAmount: number;
  /** 支付状态 */
  techServicePayStatus: EServicePayStatus;
  /** 服务费支付失败原因 */
  servicePayFailReason: string;
  /** 招商银行收方账号（技术服务费techServiceAmount） */
  zsTechServiceAmountAccount: string;
  /** 招商银行收方户名（技术服务费techServiceAmount） */
  zsTechServiceAmountAccName: string;
  /** 默认子单元余额 */
  defaultBalance: number;
  /** 招行账号 */
  bankAccount: string;
  /** 开户行 */
  openingBank: string;
}

/**
 * 支付状态
 * 支付状态(UNPAID - 未支付，DURING_PAY - 支付中，SUCCEED_PAY - 支付成功，FAIL_PAY - 支付失败，NOT_NEED - 无需支付，COMPANY_PAY - 企业向服务商支付订单总金额中，SERVER_PAY - 服务商向个人支付应到金额中，SERVICE_TAX_NOT_PAY - 服务费和税金未支付，SERVICE_TAX_DURING_PAY - 服务费或税金支付中)
 */
export enum EServicePayStatus {
  /** 未支付 */
  UNPAID = 'UNPAID',
  /** 支付中 */
  DURING_PAY = 'DURING_PAY',
  /** 支付成功 */
  SUCCEED_PAY = 'SUCCEED_PAY',
  /** 支付失败 */
  FAIL_PAY = 'FAIL_PAY',
  /** 无需支付 */
  NOT_NEED = 'NOT_NEED',
  /** 企业向服务商支付订单总金额中 */
  COMPANY_PAY = 'COMPANY_PAY',
  /** 服务商向个人支付应到金额中 */
  SERVER_PAY = 'SERVER_PAY',
  /** 服务费和税金未支付 */
  SERVICE_TAX_NOT_PAY = 'SERVICE_TAX_NOT_PAY',
  /** 服务费或税金支付中 */
  SERVICE_TAX_DURING_PAY = 'SERVICE_TAX_DURING_PAY',
}

export const servicePayStatusLabel = {
  [EServicePayStatus.UNPAID]: '未支付',
  [EServicePayStatus.DURING_PAY]: '支付中',
  [EServicePayStatus.SUCCEED_PAY]: '支付成功',
  [EServicePayStatus.FAIL_PAY]: '支付失败',
  [EServicePayStatus.NOT_NEED]: '无需支付',
  [EServicePayStatus.COMPANY_PAY]: '企业向服务商支付订单总金额中',
  [EServicePayStatus.SERVER_PAY]: '服务商向个人支付应到金额中',
  [EServicePayStatus.SERVICE_TAX_NOT_PAY]: '服务费和税金未支付',
  [EServicePayStatus.SERVICE_TAX_DURING_PAY]: '服务费或税金支付中',
};

/**
 * 服务费退款订单列表项
 */
export interface IErrRefundOrderItem extends IErrServiceFeeOrderItem {
  /** 导出状态 0未导出 1已导出 */
  techRefundExportStatus?: number;
}

/**
 * 服务费退款订单列表项
 */
export interface IErrRefundOrderParam extends IPageParam {
  /** 服务商id */
  serverId?: number;
  /** 子订单编号 */
  orderDetailIds?: number[];
}

/**
 * 服务费退款
 */
export interface IErrRefundOrder {
  /** 服务商id */
  serverId?: number;
  /** 子订单编号 */
  orderDetailIds?: number[];
  /** 回单URL */
  receiptUrl?: string;
  /** 退款金额 */
  refundAmount?: number;
}

export interface ISettlementOrderImportSelect {
  /** 人员信息 */
  orderDetailList?: ISettlementOrderImportItem[]; 
  /** 类型 */
  type?: string; 
  /** 任务id */
  dutyId?: number;
  /** 企业id */
  companyId?: number;
}

/**
 * 海外人员收款方式
*/
export enum ESettlementReceivedType {
  /** 银行卡 */
  BANK_CARD = 0,
  /** 支付宝 */
  ALIPAY = 1,
}

export const settlementReceivedTypeLabel = {
  [ESettlementReceivedType.BANK_CARD]: '银行卡',
  [ESettlementReceivedType.ALIPAY]: '支付宝',
};

/**
 * 交付物状态
 */
export enum EDeliverableStatus {
  /** 待上传 */
  TO_BE_UPLOADED = 0,
  /** 部分上传 */
  PARTIALLY_UPLOADED = 1,
  /** 已交付 */
  DELIVERED = 2,
}

export const deliverableStatusLabel = {
  [EDeliverableStatus.TO_BE_UPLOADED]: '待上传',
  [EDeliverableStatus.PARTIALLY_UPLOADED]: '部分上传',
  [EDeliverableStatus.DELIVERED]: '已交付',
};

export const deliverableStatusStyle = {
  [EDeliverableStatus.TO_BE_UPLOADED]: 'info',
  [EDeliverableStatus.PARTIALLY_UPLOADED]: 'primary',
  [EDeliverableStatus.DELIVERED]: 'success',
};

export const deliverableStatusOptions = [
  {
    label: '待上传',
    value: EDeliverableStatus.TO_BE_UPLOADED,
  },
  {
    label: '部分上传',
    value: EDeliverableStatus.PARTIALLY_UPLOADED,
  },
  {
    label: '已交付',
    value: EDeliverableStatus.DELIVERED,
  },
];

/**
 * 交付物文件项
 */
export interface IDeliverFileItem {
  /** 文件URL */
  url: string;
  /** 系统类型 */
  systemType: ESystemType;
}

/**
 * 上传交付物请求参数
 */
export interface IUploadDeliverablesParams {
  /** 订单ID */
  orderId: number;
  /** 交付物状态 */
  deliverableStatus: EDeliverableStatus;
  /** 交付物文件列表 */
  deliverFileList: IDeliverFileItem[];
}